import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "heritage-cart-v1";

const CartCtx = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {}
  }, [items]);

  const addItem = (line) => {
    setItems((prev) => {
      const idx = prev.findIndex(
        (l) => l.product_id === line.product_id && (l.variant_id || null) === (line.variant_id || null),
      );
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: Math.min(99, next[idx].qty + line.qty) };
        return next;
      }
      return [...prev, line];
    });
  };

  const updateQty = (key, qty) => {
    setItems((prev) =>
      prev
        .map((l) => {
          const lineKey = `${l.product_id}:${l.variant_id || ""}`;
          if (lineKey !== key) return l;
          return { ...l, qty: Math.max(1, Math.min(99, qty)) };
        })
        .filter((l) => l.qty > 0),
    );
  };

  const removeItem = (key) => {
    setItems((prev) => prev.filter((l) => `${l.product_id}:${l.variant_id || ""}` !== key));
  };

  const clear = () => setItems([]);

  const subtotal = useMemo(
    () => items.reduce((a, l) => a + (l.unit_price || 0) * l.qty, 0),
    [items],
  );

  const totalQty = useMemo(() => items.reduce((a, l) => a + l.qty, 0), [items]);

  const value = { items, addItem, updateQty, removeItem, clear, subtotal, totalQty };
  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export function useCart() {
  const ctx = useContext(CartCtx);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
